import { clsx } from "clsx";
import { twMerge } from "tailwind-merge"
import { getFormValueLabel } from "@/utils/formValueLabels";
import { listSeparator } from "@/utils/scriptConventions";

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

// Internal — translate an Arabic location value (city, governorate, country)
// via getFormValueLabel when a `t` function is provided; otherwise return the
// raw value untouched. Callers that don't care about translation can omit `t`.
function localize(value, t) {
  if (!value) return "";
  const s = String(value).trim();
  if (!s) return "";
  if (!t) return s;
  return getFormValueLabel(s, t);
}

// Locale-aware comma — Arabic ، when language is ar, ASCII , otherwise.

// Builds the location label for a `residence_info` object: city/governorate, with
// the country appended only when it is set and names a real country (not Egypt
// and not the 'أخرى' / "Other" sentinel, which would render meaninglessly as
// "City، Other"). See specs/forms.md INV-FORM-10.
// Safe on null/partial input — returns '' when there's nothing to show.
//
// **Phase 2.5 (2026-06-23):** prefer `formatLocationFor(residenceInfo, viewerCountry)`
// over this function — it hides the country prefix when the profile country
// matches the viewer's, and surfaces it when they differ (Muzz matrimonial
// pattern). The Egyptian-centric default below remains for any unmigrated
// call site.
//
// **Phase 3.B (2026-06-26):** added optional `t, language` params so callers
// in i18n contexts can render translated city/country names (الدار البيضاء
// → Casablanca in English) and the locale-appropriate comma. Legacy
// signature still works — when `t` is omitted, returns raw Arabic.
export function formatLocation(residenceInfo, t = null, language = null) {
  const res = residenceInfo || {};
  // 2026-07-04 UX fix: treat 'أخرى' city/governorate as invisible placeholder
  // (matches big-app UX — Muzz/Bumble/Tinder never render "Other" as a visible
  // location label). Mirrors mobile form_model.dart locationLabelFor().
  const stripOther = (v) => {
    const trimmed = v == null ? "" : String(v).trim();
    return trimmed === "أخرى" ? "" : trimmed;
  };
  const parts = [stripOther(res.city), stripOther(res.governorate)]
    .filter(Boolean)
    .map((v) => localize(v, t))
    .filter(Boolean);
  const sep = listSeparator(language);
  const base = parts.join(sep);
  const countryRaw = res.country_of_residence == null ? "" : String(res.country_of_residence).trim();
  if (countryRaw && countryRaw !== "مصر" && countryRaw !== "أخرى") {
    const countryLocalized = localize(countryRaw, t);
    if (!base) return countryLocalized; // no city/gov to show → country alone
    return `${base}${sep}${countryLocalized}`;
  }
  return base;
}

// Where she lives, as a card states it (2026-10-15). Mirrors mobile
// `ResidenceInfo.cardLocationLabel`; `cardLocation.test.js` holds both to the
// same cases.
//
// The country is left out ONLY when every card shares it - `deckCountry` is
// the deck's single country filter. Anywhere else it is shown: "Sharqia"
// alone told a viewer abroad nothing, because `formatLocation` hides Egypt.
// No city means the country alone. A missing country from an old client
// means Egypt (forms invariant).
export function formatCardLocation(residenceInfo, deckCountry = null, t = null, language = null) {
  const res = residenceInfo || {};
  const usable = (v) => {
    const trimmed = v == null ? "" : String(v).trim();
    return trimmed === "أخرى" ? "" : trimmed;
  };
  const sep = listSeparator(language);
  const base = [usable(res.city), usable(res.governorate)]
    .filter(Boolean)
    .map((v) => localize(v, t))
    .filter(Boolean)
    .join(sep);
  const raw = res.country_of_residence == null ? "" : String(res.country_of_residence).trim();
  // No country AND no city is no location at all - say nothing rather than
  // guess Egypt for it.
  if (!raw && !base) return "";
  const country = raw === "" ? "مصر" : usable(raw);
  if (!country) return base;
  if (deckCountry && String(deckCountry).trim() === country) return base;
  const countryLocalized = localize(country, t);
  return base ? `${base}${sep}${countryLocalized}` : countryLocalized;
}

// Viewer-aware location label (Phase 2.5 Item #3-B, Muzz-aligned).
//
// Decision logic:
//   profile.country == viewer.country  → city/gov only (redundant prefix hidden)
//   profile.country != viewer.country  → "city/gov، country" (cross-context)
//   profile.country missing             → city/gov only
//   viewer.country unknown              → falls back to formatLocation (legacy)
//   country is 'أخرى'                   → never rendered as a label
//
// **Phase 3.B (2026-06-26):** same locale-aware (t, language) params as
// formatLocation. Backward compatible.
export function formatLocationFor(residenceInfo, viewerCountry, t = null, language = null) {
  if (!viewerCountry || !String(viewerCountry).trim()) {
    return formatLocation(residenceInfo, t, language);
  }
  const res = residenceInfo || {};
  // 2026-07-04 UX fix: 'أخرى' city/governorate is treated as invisible placeholder
  // (see formatLocation for rationale). Mobile mirror: form_model.dart locationLabelFor().
  const stripOther = (v) => {
    const trimmed = v == null ? "" : String(v).trim();
    return trimmed === "أخرى" ? "" : trimmed;
  };
  const parts = [stripOther(res.city), stripOther(res.governorate)]
    .filter(Boolean)
    .map((v) => localize(v, t))
    .filter(Boolean);
  const sep = listSeparator(language);
  const base = parts.join(sep);
  const countryRaw = res.country_of_residence == null ? "" : String(res.country_of_residence).trim();
  if (!countryRaw || countryRaw === "أخرى") return base;
  if (countryRaw === String(viewerCountry).trim()) return base;
  const countryLocalized = localize(countryRaw, t);
  if (!base) return countryLocalized;
  return `${base}${sep}${countryLocalized}`;
}
